import { useState } from "react";

import { useTranslation } from "react-i18next";

import { BriefcaseIcon, TruckIcon } from "@heroicons/react/24/outline";

import useNotification from "../notifications/useNotifications";
import useApiHelper from "../../hooks/useApiHelper";
import { IAllJobsResponse } from "../../models/Job";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";

interface OpenVehicleJobButtonsProps {
  vehicleId: string;
  size?: "sm" | "md";
}

// The "open vehicle" / "open job" pair shown on every vehicle in the Production Dashboard. The
// overview payload doesn't carry a job id, so the job is looked up on click.
export default function OpenVehicleJobButtons({ vehicleId, size = "sm" }: OpenVehicleJobButtonsProps) {
  const { t } = useTranslation("production");
  const { get } = useApiHelper();
  const { addNotification } = useNotification();
  const [isOpeningJob, setIsOpeningJob] = useState(false);

  const buttonClasses = classNames(
    "inline-flex items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50",
    size === "sm" ? "h-5 w-5" : "h-7 w-7"
  );
  const iconClasses = size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5";

  const handleOpenVehicle = (e: React.MouseEvent) => {
    e.stopPropagation();
    openInNewTab(`/vehicles/${vehicleId}`);
  };

  const handleOpenJob = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOpeningJob) return;

    setIsOpeningJob(true);
    try {
      const response = await get<IAllJobsResponse>(`/job/vehicle/${vehicleId}`);
      const jobs = response.data ?? [];
      const latest = [...jobs].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      )[0];

      if (!latest) {
        addNotification({
          variant: "warn",
          primaryText: t("No job found for this vehicle"),
        });
        return;
      }

      openInNewTab(`/jobs/${latest.id}`);
    } catch {
      addNotification({
        variant: "error",
        primaryText: t("Unable to load job for this vehicle"),
      });
    } finally {
      setIsOpeningJob(false);
    }
  };

  return (
    <div className="flex items-center gap-x-1">
      <button
        type="button"
        onClick={handleOpenVehicle}
        className={buttonClasses}
        title={t("Open vehicle")}
        aria-label={t("Open vehicle")}
      >
        <TruckIcon className={iconClasses} />
      </button>
      <button
        type="button"
        onClick={handleOpenJob}
        disabled={isOpeningJob}
        className={buttonClasses}
        title={t("Open job")}
        aria-label={t("Open job")}
      >
        <BriefcaseIcon className={iconClasses} />
      </button>
    </div>
  );
}
